import React from 'react';
import selfieImage from '../assets/headshot.jpeg';
import sabergraphsImg from '../assets/saber-graphs/sg_home_screen.png';
import absImg from '../assets/abs-challenge/projected_wins.png';
import mlTradingImg from '../assets/ml-trading/in_sample_manual_vs_qlearner.png';
import wordDropImg from '../assets/reddit-hackathon/word_drop_drag.gif';
import unityAiImg from '../assets/unity-ai/GridLattice.png';
import resumePDF from '../assets/resume.pdf';
import { scrollTo } from '../utils/scroll';
import { caseStudyPath } from '../data/caseStudies';
import Link from './Link';

const credentials = [
    { label: 'M.S., Computer Science (AI)', detail: 'Georgia Institute of Technology' },
    { label: 'B.A., Computer Science', detail: 'Hunter College' },
];

const experiences = [
    {
        company: 'Fetch & Fable',
        role: 'Software Engineer',
        period: 'May 2026 - Present',
        where: 'New York, NY',
        bullets: [
            <>
                Built and launched{' '}
                <a
                    className="intro-link"
                    href="https://www.fetchandfable.co/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    fetchandfable.co
                </a>
                , a full-stack e-commerce web app turning user dog photos into custom
                illustrated printed books; delivered from initial spec to paying
                customers in under three months.
            </>,
            'Built custom Klaviyo event webhooks for automated abandoned-cart and post-purchase email flows, driving 25% of attributed revenue.',
            'Engineered a crash-safe Postgres job queue and multi-model AI pipeline (Claude and Gemini) to reliably process multi-minute book generations without dropping orders during traffic spikes.',
        ],
        tech: ['Python', 'TypeScript', 'Claude', 'Gemini', 'Postgres', 'Docker', 'Stripe', 'Klaviyo'],
        caseStudy: 'fetch-and-fable',
        liveUrl: 'https://www.fetchandfable.co/',
    },
    {
        company: 'Grizz the Beagle',
        role: 'Software Engineer (Contract)',
        period: 'May 2025 - May 2026',
        where: 'New York, NY',
        bullets: [
            'Scoped and shipped a production RAG support chatbot (FastAPI, Claude API) embedded in the Shopify storefront; it now handles 80% of customer inquiries and cut response time from 8+ hours to under 10 seconds.',
            'Replaced manual cross-platform reporting with a nightly-ETL data warehouse and a Next.js dashboard reporting blended ROAS and ROI across Shopify, Meta Ads, and the chatbot.',
        ],
        tech: ['Python', 'FastAPI', 'Claude', 'Postgres', 'Next.js', 'Shopify'],
        caseStudy: 'grizz-the-beagle',
        liveUrl: 'https://grizzthebeagle.com/',
    },
    {
        company: 'Varsity Tutors',
        role: 'Computer Science Tutor',
        period: 'Jun 2025 - May 2026',
        where: 'New York, NY',
        bullets: [
            'Mentor students from middle school through university in Python, OOP, and algorithms through pair programming, live code reviews, and interactive debugging sessions.',
            'Create custom assignments and study plans tailored to each student\'s gaps, helping them get up to speed for university-level coursework.',
        ],
        tech: ['Python', 'OOP', 'Algorithms'],
    },
    {
        company: 'Two Sigma',
        role: 'Software Engineering Mentee',
        period: 'Jul 2021 - Aug 2021',
        where: 'New York, NY',
        bullets: [
            'Contributed to pip, the Python package manager, by submitting pull requests fixing uninstallation bugs.',
            'Built a CLI tool for dependency debugging that maps source files to their origin packages; presented the final project to an engineering panel.',
        ],
        tech: ['Python', 'Open Source', 'CLI'],
    },
];

// Flip this to true to show the "Games & Systems" project group again.
const SHOW_GAMES_PROJECTS = false;

const projects = [
    {
        id: 'sabergraphs',
        title: 'SaberGraphs',
        blurb: 'Full-stack AI app that answers baseball questions with interactive charts, making advanced analytics accessible to non-technical users.',
        year: 2026,
        category: 'AI / Machine Learning',
        status: 'Live',
        tech: ['Python', 'JavaScript', 'React', 'FastAPI', 'PostgreSQL', 'Claude API', 'Docker'],
        media: sabergraphsImg,
        liveLink: 'https://sabergraphs.com',
        github: 'https://github.com/cmartinez131/sabergraphs',
        caseStudy: 'sabergraphs',
    },
    {
        id: 'abs',
        title: 'ABS Challenge Strategy',
        blurb: "Reinforcement learning applied to MLB's new robot-umpire challenge system. Presented at the SABR Analytics Conference 2026.",
        year: 2026,
        category: 'AI / Machine Learning',
        status: 'Presented at SABR 2026',
        tech: ['Python', 'Q-Learning', 'pybaseball'],
        media: absImg,
        liveLink: null,
        github: 'https://github.com/cmartinez131/sabr-abs-challenge',
    },
    {
        id: 'mlb-api',
        title: 'MLB Analytics API',
        blurb: "Pulls live MLB stats and pitch-tracking data, computes a custom \u201cVeteran Presence\u201d score for any player, and serves the result to any app that asks for it.",
        year: 2025,
        category: 'Full-Stack',
        status: 'Deployed',
        tech: ['FastAPI', 'pandas', 'Docker', 'AWS'],
        media: null,
        liveLink: null,
        github: 'https://github.com/cmartinez131/umpire-batter-analytics-api',
    },
    {
        id: 'batting-cage',
        title: 'Batting Cage Booking App',
        blurb: 'Online booking system for a batting-cage business. Customers can reserve cage time 24/7, sign in securely, and find the location on an embedded map.',
        year: 2023,
        category: 'Full-Stack',
        status: 'Live',
        tech: ['React', 'Firebase', 'Tailwind'],
        media: null,
        liveLink: 'https://batting-cage-app.web.app/',
        github: 'https://github.com/cmartinez131/batting-cage-business-website',
    },
    {
        id: 'sketch',
        title: 'Real-Time Drawing Game',
        blurb: 'Real-time multiplayer drawing and guessing game, similar to Pictionary. Players take turns sketching prompts while others try to guess, with live chat and round scoring.',
        year: 2023,
        category: 'Full-Stack',
        status: null,
        tech: ['React', 'Node', 'Socket.IO', 'MongoDB'],
        media: null,
        liveLink: null,
        github: 'https://github.com/cmartinez131/sketch',
    },
    {
        id: 'ml-trading',
        title: 'ML Trading Strategy',
        blurb: 'Compared a rules-based trading strategy to a reinforcement-learning agent on JPMorgan stock. The learned agent beat the benchmark on both training and test data.',
        year: 2024,
        category: 'AI / Machine Learning',
        status: 'Course',
        tech: ['Python', 'Q-Learning', 'Matplotlib'],
        media: mlTradingImg,
        liveLink: null,
        github: null,
    },
    {
        id: 'word-drop',
        title: 'Word Drop',
        blurb: 'Word game that plays inside a Reddit post, with a live global leaderboard. Built for the Reddit Developer Platform hackathon.',
        year: 2024,
        category: 'Games & Systems',
        status: 'Hackathon',
        tech: ['JavaScript', 'Redis', 'Devvit'],
        media: wordDropImg,
        liveLink: 'https://www.reddit.com/r/WordDrop/comments/1hgk1iz/word_drop/',
        github: 'https://github.com/cmartinez131/word-drop',
    },
    {
        id: 'unity-ai',
        title: 'Unity Path-Planning AI',
        blurb: 'Pathfinding framework for video game characters. Implements the core navigation techniques used to route NPCs around obstacles in modern games.',
        year: 2025,
        category: 'Games & Systems',
        status: 'Coursework',
        tech: ['C#', 'Unity', 'A*', 'Comp. Geometry'],
        media: unityAiImg,
        liveLink: null,
        github: null,
    },
];

const visibleProjects = projects.filter(
    (p) => SHOW_GAMES_PROJECTS || p.category !== 'Games & Systems'
);

const Intro = () => {
    return (
        <div id="intro-section" className="recruiter-layout">
            <aside className="rec-sidebar">
                <div className="rec-sidebar-inner">
                    <img
                        className="rec-avatar"
                        src={selfieImage}
                        alt="Christopher Martinez"
                    />
                    <h1 className="rec-name">Christopher Martinez</h1>
                    <p className="rec-role">Software Engineer</p>

                    <div className="rec-side-section">
                        <div className="rec-side-label">Contact</div>
                        <a
                            className="rec-side-link"
                            href="mailto:chrismartinez131@gmail.com"
                        >
                            chrismartinez131@gmail.com
                        </a>
                        <a
                            className="rec-side-link"
                            href="https://github.com/cmartinez131"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            github.com/cmartinez131
                        </a>
                        <a
                            className="rec-side-link"
                            href="https://www.linkedin.com/in/cmartinez131"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            linkedin.com/in/cmartinez131
                        </a>
                        <div className="rec-side-location">New York, NY</div>
                        <a
                            className="rec-side-link"
                            href={resumePDF}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Resume (PDF) ↗
                        </a>
                    </div>

                    <div className="rec-side-section">
                        <div className="rec-side-label">Education</div>
                        {credentials.map((c) => (
                            <div key={c.label} className="rec-cred-row">
                                <div className="rec-cred-label">{c.label}</div>
                                <div className="rec-cred-detail">{c.detail}</div>
                            </div>
                        ))}
                    </div>

                    <a
                        className="rec-resume-btn"
                        href="#experience"
                        onClick={(e) => {
                            e.preventDefault();
                            scrollTo('experience');
                        }}
                    >
                        See my work ↓
                    </a>
                </div>
            </aside>

            <main className="rec-main">
                <section className="rec-about">
                    <div className="rec-section-head">
                        <h2 className="rec-h2">About</h2>
                        <div className="rec-line" />
                    </div>
                    <p className="rec-bio-para">
                        I’m a software engineer finishing my M.S. in Computer Science (AI)
                        at the Georgia Institute of Technology. I build customer-focused AI
                        and data products end to end: shipping the full-stack software,
                        defining feature specs, and using product analytics to guide iteration.
                    </p>
                    <p className="rec-bio-para">
                        Recently, I built and launched{' '}
                        <a
                            className="intro-link"
                            href="https://www.fetchandfable.co/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Fetch &amp; Fable
                        </a>
                        , an e-commerce web app serving paying customers since
                        July 2026, and shipped a production RAG support chatbot and
                        cross-platform analytics dashboard for Grizz the Beagle, an
                        e-commerce brand. I’m also a lifelong baseball fan, which
                        inspired{' '}
                        <a
                            className="intro-link"
                            href="https://sabergraphs.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            SaberGraphs
                        </a>{' '}
                        and the reinforcement learning research I presented at the{' '}
                        <a
                            className="intro-link"
                            href="https://sabr.org/analytics"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            SABR Analytics Conference 2026
                        </a>
                        .
                    </p>
                </section>

                <section id="experience" className="rec-experience">
                    <div className="rec-section-head">
                        <h2 className="rec-h2">Experience</h2>
                        <div className="rec-line" />
                    </div>
                    <div className="rec-exp-list">
                        {experiences.map((e) => (
                            <article key={e.company} className="rec-exp-card">
                                <div className="rec-exp-head">
                                    <div className="rec-exp-head-left">
                                        <h3 className="rec-exp-title">{e.company}</h3>
                                        <div className="rec-exp-role">{e.role}</div>
                                    </div>
                                    <div className="rec-exp-meta">
                                        <div className="rec-exp-period">{e.period}</div>
                                        <div className="rec-exp-where">{e.where}</div>
                                    </div>
                                </div>
                                <ul className="rec-exp-bullets">
                                    {e.bullets.map((b, i) => (
                                        <li key={i} className="rec-exp-bullet">{b}</li>
                                    ))}
                                </ul>
                                {e.tech.length > 0 && (
                                    <div className="rec-exp-tech-row">
                                        {e.tech.map((t) => (
                                            <span key={t} className="rec-exp-tech">{t}</span>
                                        ))}
                                    </div>
                                )}
                                {(e.caseStudy || e.liveUrl) && (
                                    <div className="rec-exp-links">
                                        {e.caseStudy && (
                                            <Link
                                                className="rec-primary-btn"
                                                href={caseStudyPath(e.caseStudy)}
                                            >
                                                Read the case study →
                                            </Link>
                                        )}
                                        {e.liveUrl && (
                                            <a
                                                className="rec-ghost-btn"
                                                href={e.liveUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Live site ↗
                                            </a>
                                        )}
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>
                </section>

                <section id="projects" className="rec-projects">
                    <div className="rec-section-head">
                        <h2 className="rec-h2">Projects</h2>
                        <div className="rec-line" />
                    </div>
                    <div className="rec-grid">
                        {visibleProjects.map((p) => (
                            <article key={p.id} className="rec-card">
                                <div className="rec-card-img">
                                    {p.media ? (
                                        <img src={p.media} alt="" />
                                    ) : (
                                        <div className="rec-card-placeholder">{p.title}</div>
                                    )}
                                    {p.status && (
                                        <div className="rec-status-tag">{p.status}</div>
                                    )}
                                </div>
                                <div className="rec-card-body">
                                    <div className="rec-card-meta">
                                        <span>{p.year}</span>
                                        <span className="rec-card-meta-dot">·</span>
                                        <span>{p.category}</span>
                                    </div>
                                    <h3 className="rec-card-title">{p.title}</h3>
                                    <p className="rec-card-blurb">{p.blurb}</p>
                                    <div className="rec-exp-tech-row">
                                        {p.tech.map((t) => (
                                            <span key={t} className="rec-exp-tech">{t}</span>
                                        ))}
                                    </div>
                                    <div className="rec-card-links">
                                        {p.liveLink && (
                                            <a
                                                className="rec-primary-btn"
                                                href={p.liveLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Live ↗
                                            </a>
                                        )}
                                        {p.github && (
                                            <a
                                                className="rec-ghost-btn"
                                                href={p.github}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Code ↗
                                            </a>
                                        )}
                                        {p.caseStudy && (
                                            <Link
                                                className="rec-ghost-btn"
                                                href={caseStudyPath(p.caseStudy)}
                                            >
                                                Case study →
                                            </Link>
                                        )}
                                        {!p.liveLink && !p.github && (
                                            <span className="rec-coursework-note">
                                                Coursework · code not public
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>
            </main>
        </div>
    );
};

export default Intro;
